import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {LoaderService} from '@app/services/loader.service';
import {
  OrganizationsList,
  OrganizationsListSuccess,
  OrganizationsListError,
  OrganizationDelete,
  OrganizationDeleteSuccess,
  OrganizationDeleteError,
  OrganizationUpdate,
  OrganizationUpdateSuccess,
  OrganizationUpdateError,
  OrganizationAdd,
  OrganizationAddSuccess,
  OrganizationAddError,
  OrganizationsListAll,
  OrganizationsListAllSuccess,
  OrganizationsListAllError,
  OrganizationGet,
  OrganizationGetSuccess,
  OrganizationGetError
} from '@app/stores/organization/organization.actions';
import {IOrganizationState} from '@app/stores/organization/organization.state';
import {OrganizationService} from '@app/services/organization.service';

@Injectable()
export class OrganizationEffects {
  private _actions$ = inject(Actions);
  private _store = inject<Store<IOrganizationState>>(Store);
  private organizationService = inject(OrganizationService);
  private loaderService = inject(LoaderService);


  organizationsAllList$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationsListAll),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.getOrganizations(action.params).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationsListAllSuccess({organizations: resp.data});
          }
          this.loaderService.hide(true);
          return OrganizationsListAllError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationsListAllError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  organizationsList$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationsList),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.getOrganizations(action.params).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationsListSuccess({paginatedOrganizations: resp.data});
          }
          this.loaderService.hide(true);
          return OrganizationsListError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationsListError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  organizationDelete$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationDelete),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.deleteOrganization(action.organizationId).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationDeleteSuccess();
          }
          this.loaderService.hide(true);
          return OrganizationDeleteError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationDeleteError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  organizationUpdate$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationUpdate),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.updateOrganization(action.organizationId, action.organization).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationUpdateSuccess();
          }
          this.loaderService.hide(true);
          return OrganizationUpdateError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationUpdateError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  organizationAdd$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationAdd),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.addOrganization(action.organization).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationAddSuccess();
          }
          this.loaderService.hide(true);
          return OrganizationAddError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationAddError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  organizationGet$ = createEffect(() => this._actions$.pipe(
    ofType(OrganizationGet),
    switchMap((action) => {
      this.loaderService.show();
      return this.organizationService.getOrganization(action.organizationId).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return OrganizationGetSuccess({organization: resp.data});
          }
          this.loaderService.hide(true);
          return OrganizationGetError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(OrganizationGetError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));
}
